import { useContext, useEffect, useRef, useState } from 'react';
import { Card, Spin } from 'antd';
import { ThemeContext } from '../../../../contexts/ThemeContext';

interface StatCardProps {
    title: string;
    mainValue: string;
    numericValue?: number;
    formatter?: (v: number) => string;
    subtitle?: string;
    secondaryLabel?: string;
    secondaryValue?: string;
    isLoading: boolean;
    color: string;
    bgColor: string;
    icon: React.ReactNode;
}

export default function StatCard({ title, mainValue, numericValue, formatter, subtitle, secondaryLabel, secondaryValue, isLoading, color, bgColor, icon }: StatCardProps) {
    const { theme } = useContext(ThemeContext);
    const [isSmall, setIsSmall] = useState(window.innerWidth < 500);
    const [animatedValue, setAnimatedValue] = useState(0);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        const handler = () => setIsSmall(window.innerWidth < 500);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    useEffect(() => {
        if (numericValue === undefined || isLoading) {
            setAnimatedValue(0);
            return;
        }
        const duration = 1200;
        const start = performance.now();
        const target = numericValue;
        const animate = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setAnimatedValue(Math.max(0, eased * target));
            if (progress < 1) rafRef.current = requestAnimationFrame(animate);
        };
        rafRef.current = requestAnimationFrame(animate);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [numericValue, isLoading]);

    const displayValue = numericValue !== undefined && formatter
        ? formatter(animatedValue)
        : mainValue;

    return (
        <Card style={{ border: 'none', borderLeft: `6px solid ${color}`, borderRadius: 8, minHeight: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }} bodyStyle={{ padding: 20 }}>
            <div className="flex justify-between items-start gap-4">
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ color, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                        {title}
                    </div>
                    {isLoading ? (
                        <Spin />
                    ) : (
                        <>
                            <div style={{ fontSize: isSmall ? 24 : 24, fontWeight: 700, lineHeight: 1.2, overflowWrap: 'break-word' }}>{displayValue}</div>
                            {subtitle && (
                                <div style={{ color: '#888', fontSize: 12, marginTop: 2, overflowWrap: 'break-word' }}>{subtitle}</div>
                            )}
                            {secondaryLabel && (
                                <div style={{ color: '#888', fontSize: 13, marginTop: 6, overflowWrap: 'break-word' }}>
                                    {secondaryLabel}: <strong style={{ color: theme === 'light' ? '#555' : '#aaa' }}>{secondaryValue}</strong>
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div
                    style={{
                        background: color,
                        borderRadius: '50%',
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: 22,
                        color: '#fff',
                    }}
                >
                    {icon}
                </div>
            </div>
        </Card>
    );
}
